import { useRuntimeConfig } from '#imports';
import { merge } from 'lodash-es';
import type { MappedResponseType } from 'ofetch';
import type {
  BackendResponse,
  BackendCouponInfoResponse,
  BackendOrderResponse,
  DcbVouchers,
} from '~/lib/shop-api';

export function useShopApi() {
  const { $shopBackendApi } = useNuxtApp();
  const {
    public: { baseUrl },
  } = useRuntimeConfig();

  function addAfterPaymentUrls(
    orderParams: Record<string, unknown> | undefined
  ): Record<string, unknown> {
    const afterPaymentUrls = {
      brodosSuccessUrl: new URL('/api/brodos-response/success', baseUrl).href,
      brodosFailureUrl: new URL('/api/brodos-response/failure', baseUrl).href,
      novalnetSuccessUrl: new URL('/api/brodos-response/success', baseUrl).href,
      novalnetFailureUrl: new URL('/api/brodos-response/failure', baseUrl).href,
      confirmationUrl: new URL('/shop/bestaetigung', baseUrl).href,
      telekomPaymentInformationUrl: new URL('/shop/telekom-bezahlung-information', baseUrl).href,
      notifyUrl: new URL('/api/computop-response/notify', baseUrl).href,
      dimocoNotifyUrl: new URL('/api/dimoco-response/notify', baseUrl).href,
      successUrlTelekom: new URL('/shop/telekom-zahlungsverifizierung', baseUrl).href,
      successUrlBrodos: new URL('/shop/brodos-zahlungsverifizierung', baseUrl).href,
      successUrl: new URL('/shop/zahlungsverifizierung', baseUrl).href,
      failureUrl: new URL('/shop/fehler', baseUrl).href,
    };
    return merge({}, orderParams, afterPaymentUrls);
  }

  return {
    availabilitySubscriptions(subscription: any) {
      return $shopBackendApi<any>('/api/shop/availability_subscriptions', {
        method: 'POST',
        body: { subscription },
      });
    },
    submitOrder(orderParams: SubmitOrder) {
      const paramsWithAfterPaymentUrls = addAfterPaymentUrls(orderParams);
      return $shopBackendApi<BackendOrderResponse>('/api/shop/order', {
        method: 'POST',
        body: { order: paramsWithAfterPaymentUrls },
      });
    },
    submitPaypalExpress(
      orderParams: Record<string, unknown>
    ): Promise<MappedResponseType<'json', any>> {
      const paramsWithAfterPaymentUrls = addAfterPaymentUrls(orderParams);
      return $shopBackendApi('/api/shop/paypal_express', {
        method: 'PATCH',
        body: { order: paramsWithAfterPaymentUrls },
      });
    },
    confirmOrder(): Promise<MappedResponseType<'json', any>> {
      return $shopBackendApi('/api/shop/confirmation');
    },
    deleteOrder(): Promise<MappedResponseType<'json', any>> {
      return $shopBackendApi('/api/shop/order', { method: 'DELETE' });
    },
    getCurrentCart(showLoadingSpinner = true) {
      return $shopBackendApi<BackendResponse>('/api/shop/order', {
        loadingSpinner: showLoadingSpinner,
      });
    },
    items(items: { quantity?: number; articleNumber: string }[]) {
      return $shopBackendApi<BackendResponse>('/api/shop/items', {
        method: 'POST',
        body: { items },
      });
    },
    addCouponToCart(redeemedCoupon: string) {
      return $shopBackendApi<BackendResponse>('/api/shop/coupon', {
        method: 'PATCH',
        body: { order: { redeemedCoupon } },
      });
    },
    deleteCoupon(): Promise<MappedResponseType<'json', any>> {
      return $shopBackendApi('/api/shop/coupon', { method: 'DELETE' });
    },
    getCouponInfo(couponValue: string) {
      return $shopBackendApi<BackendCouponInfoResponse>('/api/shop/coupon?redeemed_coupon', {
        method: 'GET',
        params: { redeemed_coupon: couponValue },
      });
    },
    updateCustomerData(order: Partial<Cart>) {
      return $shopBackendApi<BackendResponse>('/api/shop/order', {
        method: 'PATCH',
        body: { order },
      });
    },
    revertOrderToCart(): Promise<MappedResponseType<'json', any>> {
      return $shopBackendApi('/api/shop/revert_order_to_cart', {});
    },
    splitJointCart(): Promise<Record<'order', Cart>> {
      return $shopBackendApi('/api/shop/split_joint_cart', {});
    },
    dcbVouchers(options: DcbVouchers) {
      return $shopBackendApi<{ status?: 'verified'; error?: string }>('/api/shop/dcb_vouchers/', {
        method: 'POST',
        body: {
          ...options,
          frontend_url: options.path ? `${baseUrl}${options.path}` : undefined,
          customer_override: options.customerOverride,
        },
      });
    },
    dcbVouchersDOI(doubleOptinToken: string) {
      return $shopBackendApi<{ status?: 'verified'; error?: string }>(
        '/api/shop/dcb_vouchers/confirm',
        {
          method: 'POST',
          body: { double_optin_token: doubleOptinToken },
        }
      );
    },
    shareCart(email: string) {
      return $shopBackendApi<{ status: 'Cart send.'; error?: any }>(
        '/api/shop/share_carts/confirm',
        {
          method: 'POST',
          body: { email, shop_url: `${baseUrl}/shop/warenkorb` },
        }
      );
    },
  };
}
